/* Copy da tela "Como Funciona" (guia do Portal do Publisher, ADR-0724 + ADR-0728). O parágrafo
   informativo é quebrado em vários campos porque duas palavras (o nome da seção e "publicados")
   aparecem em negrito no JSX — uma única string perderia a marcação. Content independente do
   `faq.ts` (outra área) mesmo com textos parecidos (ex.: "Todos", labels de status). */
import type { FaqStatus } from '@planuze/admin-contracts';
import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

interface PublisherGuideContent {
  readonly pageTitle: string;
  readonly pageDescription: string;
  readonly newTopicButton: string;
  readonly infoIntro: string;
  readonly howItWorksLabel: string;
  readonly infoMiddle: string;
  readonly infoAfterCategory: string;
  readonly publishedWord: string;
  readonly infoAfterPublished: string;
  readonly searchAriaLabel: string;
  readonly searchPlaceholder: string;
  readonly statusFilterAriaLabel: string;
  readonly allStatusesLabel: string;
  /** Rótulo por status (enum fixo do contrato — não deduzido dos dados). */
  readonly statusLabels: Readonly<Record<FaqStatus, string>>;
  readonly loadError: string;
  readonly loadMoreButton: string;
  readonly deleteDialogTitle: string;
  readonly deleteDialogDescription: (slug: string) => string;
  readonly deleteConfirmButton: string;
  readonly deleteSuccessToast: string;
  readonly deleteErrorToast: string;
}

const PUBLISHER_GUIDE_CONTENT: Bilingual<PublisherGuideContent> = {
  'pt-BR': {
    pageTitle: 'Como Funciona (Portal do Publisher)',
    pageDescription:
      'Guia editorial exibido no Portal do Publisher do app. Conteúdo editável — não hardcoded.',
    newTopicButton: 'Novo tópico',
    infoIntro: 'Estes tópicos alimentam a seção ',
    howItWorksLabel: '"Como Funciona"',
    infoMiddle: ' do Portal do Publisher (no app), na categoria fixa ',
    infoAfterCategory: '. Cada tópico tem variações por idioma (badges); só idiomas ',
    publishedWord: 'publicados',
    infoAfterPublished:
      ' aparecem para o usuário. A resposta aceita Markdown enriquecido (títulos, listas, código, ' +
      'callouts, mermaid). Edições ficam na trilha de auditoria.',
    searchAriaLabel: 'Buscar tópicos do guia do publisher',
    searchPlaceholder: 'Buscar por pergunta ou slug…',
    statusFilterAriaLabel: 'Filtrar por status',
    allStatusesLabel: 'Todos',
    statusLabels: { draft: 'Rascunho', published: 'Publicado' },
    loadError: 'Falha ao carregar o guia do publisher.',
    loadMoreButton: 'Carregar mais',
    deleteDialogTitle: 'Remover tópico',
    deleteDialogDescription: (slug) =>
      `Remover "${slug}" e todas as suas traduções? Esta ação não pode ser desfeita.`,
    deleteConfirmButton: 'Remover',
    deleteSuccessToast: 'Tópico removido.',
    deleteErrorToast: 'Falha ao remover o tópico.',
  },
  'en-US': {
    pageTitle: 'How It Works (Publisher Portal)',
    pageDescription:
      "Editorial guide shown in the app's Publisher Portal. Editable content — not hardcoded.",
    newTopicButton: 'New topic',
    infoIntro: 'These topics feed the ',
    howItWorksLabel: '"How It Works"',
    infoMiddle: ' section of the Publisher Portal (in the app), under the fixed category ',
    infoAfterCategory: '. Each topic has per-language variations (badges); only ',
    publishedWord: 'published',
    infoAfterPublished:
      ' languages appear to the user. The answer accepts rich Markdown (headings, lists, code, ' +
      'callouts, mermaid). Edits are tracked in the audit trail.',
    searchAriaLabel: 'Search publisher guide topics',
    searchPlaceholder: 'Search by question or slug…',
    statusFilterAriaLabel: 'Filter by status',
    allStatusesLabel: 'All',
    statusLabels: { draft: 'Draft', published: 'Published' },
    loadError: 'Failed to load the publisher guide.',
    loadMoreButton: 'Load more',
    deleteDialogTitle: 'Remove topic',
    deleteDialogDescription: (slug) =>
      `Remove "${slug}" and all its translations? This action cannot be undone.`,
    deleteConfirmButton: 'Remove',
    deleteSuccessToast: 'Topic removed.',
    deleteErrorToast: 'Failed to remove the topic.',
  },
};

export const usePublisherGuideContent = (): PublisherGuideContent =>
  useBilingual(PUBLISHER_GUIDE_CONTENT);
